import { X } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { PageHero } from '../components/PageHero'
import { ResponsiveImage } from '../components/ResponsiveImage'
import { galleryImages, images, type GalleryImage } from '../data/images'

const galleryFormats = [
  'square',
  'square',
  'square',
  'square',
  'portrait',
  'portrait',
  'portrait',
  'portrait',
  'portrait',
] as const

export function OurSpace() {
  const [selected, setSelected] = useState<GalleryImage | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const openerRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!selected) return
    const frame = requestAnimationFrame(() => closeButtonRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [selected])

  const closeLightbox = () => {
    setSelected(null)
    requestAnimationFrame(() => openerRef.current?.focus())
  }

  const handleLightboxKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeLightbox()
    }
    if (event.key === 'Tab') {
      event.preventDefault()
      closeButtonRef.current?.focus()
    }
  }

  return (
    <>
      <PageHero eyebrow="Take a Look Inside" title="Our Space" image={images['gallery-02']} objectPosition="48% center" />
      <section className="section shell gallery-section">
        <div className="section-heading reveal reveal--heading">
          <span className="eyebrow">The Salon</span>
          <h2>A polished retreat in San Juan Capistrano.</h2>
          <p>Bright styling rooms, quiet treatment spaces, warm hospitality, and all the details that make an appointment feel special.</p>
        </div>
        <div className="gallery-editorial">
          {galleryImages.map((item, index) => (
            <figure
              className={`gallery-figure gallery-figure--${galleryFormats[index] ?? 'portrait'}${index === galleryImages.length - 1 && galleryImages.length % 2 !== 0 ? ' gallery-figure--finale' : ''} reveal reveal--image reveal--delay-${index % 2}`}
              key={item.alt}
            >
              <button type="button" onClick={(event) => { openerRef.current = event.currentTarget; setSelected(item) }} aria-label={`Open photo: ${item.alt}`}>
                <ResponsiveImage image={item.image} alt={item.alt} sizes="(max-width: 700px) 100vw, 48vw" objectPosition={item.objectPosition} />
                <span>View photograph</span>
              </button>
              <figcaption><span>{String(index + 1).padStart(2, '0')}</span>{item.alt}</figcaption>
            </figure>
          ))}
        </div>
      </section>
      {selected && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={`Gallery image: ${selected.alt}`} onClick={closeLightbox} onKeyDown={handleLightboxKeyDown}>
          <button ref={closeButtonRef} type="button" onClick={closeLightbox} aria-label="Close image"><X aria-hidden="true" /></button>
          <ResponsiveImage image={selected.image} alt={selected.alt} sizes="(max-width: 1240px) 100vw, 1200px" objectPosition={selected.objectPosition} priority onClick={(event) => event.stopPropagation()} />
        </div>
      )}
    </>
  )
}
